import React, { useCallback, useEffect, useRef, useState } from 'react';

import '../../../util_components/custom_modal/modal.css';
import { FOCUSABLE_SELECTOR, focus_left_dialog, is_focus_lost, trap_index } from './dialog_logic';

/*
 * PROD-7828 — the dialog shell for every teacher-payments dialog. Same markup and classes as the shared
 * CustomModal (an optional title beside the ✕), plus what a money dialog owes a keyboard/SR user:
 * role="dialog" + aria-modal, an initial focus, a focus trap, focus returned to whatever opened it, a
 * named close control, Escape for the top-most dialog only, and errors announced.
 * Deliberately local: CustomModal has ~90 consumers, several of which portal content out of the modal
 * (this module's own why_popover does), and a focus trap there cannot be proven safe for all of them.
 * `title` => rendered as the heading and names the dialog; `title_id` => named by the caller's own heading.
 * `show` => for callers that keep the dialog mounted; a conditionally rendered dialog leaves it true.
 * `guard_text` non-empty => Escape and the overlay refuse to discard; Cancel and ✕ still close.
 * `busy` => a request is in flight: the ✕ is disabled and Escape/overlay do nothing, so its answer
 * always lands in the dialog that sent it (R6H-M1).
 * `on_focus_lost` => called on close when the opener has gone meanwhile, so the caller picks a target.
 */

// Open dialogs, oldest first: Escape belongs to the top-most one only (a confirm over an Edit).
const open_stack = [];

const TpDialog = ({
  show = true,
  title = '',
  title_id,
  guard_text = '',
  busy = false,
  on_close,
  on_focus_lost,
  children
}) => {
  const panel_ref = useRef(null);
  const opener_ref = useRef(null);
  const guard_ref = useRef(guard_text);
  const busy_ref = useRef(busy);
  const [blocked, setBlocked] = useState(false);
  guard_ref.current = guard_text;
  busy_ref.current = busy;

  const focusables = () =>
    panel_ref.current ? Array.from(panel_ref.current.querySelectorAll(FOCUSABLE_SELECTOR)) : [];

  // Escape/overlay are "accidental" closes; the explicit affordances go straight to on_close.
  const dismiss = useCallback(() => {
    if (busy_ref.current) return;
    if (guard_ref.current) {
      setBlocked(true);
      return;
    }
    on_close();
  }, [on_close]);
  // Parents pass inline callbacks, so the open/close effect keys off `show` alone and reads the
  // current handlers through refs — otherwise every re-render would re-grab focus.
  const dismiss_ref = useRef(dismiss);
  dismiss_ref.current = dismiss;
  const lost_ref = useRef(on_focus_lost);
  lost_ref.current = on_focus_lost;

  useEffect(() => {
    if (!show) return undefined;
    const me = {};
    open_stack.push(me);
    opener_ref.current = document.activeElement;
    const first = focusables()[0] || panel_ref.current;
    if (first) first.focus({ preventScroll: true });
    const on_key = (e) => {
      if (open_stack[open_stack.length - 1] !== me) return;
      if (e.key === 'Escape' || e.keyCode === 27) dismiss_ref.current();
    };
    document.addEventListener('keydown', on_key);
    // R6H-L4: focus moved outside (a Tab from <body> lands behind the overlay) is pulled back. Focus
    // inside another dialog is left alone: a popover portaled out of this one owns its own focus.
    const on_focus_in = (e) => {
      const panel = panel_ref.current;
      if (!panel || !focus_left_dialog(e.target, document.body, panel)) return;
      (focusables()[0] || panel).focus({ preventScroll: true });
    };
    document.addEventListener('focusin', on_focus_in);
    // A focused control that unmounts drops focus to <body> without any focus event; the dialog takes it.
    const observer = new MutationObserver(() => {
      const panel = panel_ref.current;
      if (panel && is_focus_lost(document.activeElement, document.body)) {
        panel.focus({ preventScroll: true });
      }
    });
    if (panel_ref.current) observer.observe(panel_ref.current, { childList: true, subtree: true });
    return () => {
      document.removeEventListener('keydown', on_key);
      document.removeEventListener('focusin', on_focus_in);
      observer.disconnect();
      open_stack.splice(open_stack.indexOf(me), 1);
      // The trigger is usually still mounted; if a reload unmounted it (or it was never focused, as a
      // Safari click leaves it), the caller decides where focus goes.
      const opener = opener_ref.current;
      if (!is_focus_lost(opener, document.body) && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      } else if (lost_ref.current) lost_ref.current();
      setBlocked(false);
    };
  }, [show]);

  // R7RH-F1: Chrome blurs a focused control that a busy flip disabled only when it renders, after the
  // observer above has run, so a frame later the dialog takes back focus that left it.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const panel = panel_ref.current;
      if (panel && focus_left_dialog(document.activeElement, document.body, panel)) {
        panel.focus({ preventScroll: true });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [busy]);

  if (!show) return null;

  const on_key_down = (e) => {
    if (e.key !== 'Tab') return;
    const list = focusables();
    // A portaled child (why_popover) owns its own focus — never fight it.
    if (document.activeElement && !panel_ref.current.contains(document.activeElement)) return;
    const idx = trap_index(list, document.activeElement, e.shiftKey);
    if (idx === -1) return;
    e.preventDefault();
    list[idx].focus({ preventScroll: true });
  };

  return (
    // The overlay is pointer-only (its keyboard equivalent is Escape); a click that started inside the
    // panel has another target, so only a click on the overlay itself dismisses.
    <div
      className="custom-modal-container"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div
        ref={panel_ref}
        role="dialog"
        aria-modal="true"
        aria-label={title || undefined}
        aria-labelledby={title ? undefined : title_id}
        tabIndex={-1}
        className="custom-modal-inner-container w-full max-w-[900px]"
        onKeyDown={on_key_down}
      >
        <div className={title ? 'flex justify-between' : 'flex justify-end'}>
          {title ? <div className="text-xl font-semibold mb-6">{title}</div> : null}
          <button
            type="button"
            className="tp-dialog-x"
            aria-label={title ? `Close ${title}` : 'Close'}
            onClick={on_close}
            disabled={busy}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 384 512"
              fill="currentColor"
              className="w-6 h-6 cursor-pointer"
              aria-hidden="true"
            >
              <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z" />
            </svg>
          </button>
        </div>
        {blocked && guard_text ? (
          <div className="tp-banner tp-banner-warn" role="alert">
            {guard_text}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
};

export default TpDialog;
